import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStudentApi } from '../api';
import { studentKeys } from '../query-key-factory';
import { FeeResponse, UpdateFeeRequest } from './fees.types';

// ----- Unarchive Fees -----
const useUnarchiveFees = () => {
  const StudentApi = useStudentApi();
  const queryClient = useQueryClient();

  const unarchiveFees = async ({ causeId, uuids }: { causeId: string; uuids: string[] }) => {
    const { data } = await StudentApi.put<FeeResponse>(`/fees/unarchive?cause_id=${causeId}`, {
      uuids,
      cause_id: causeId,
    });
    return data;
  };

  return useMutation({
    mutationFn: unarchiveFees,
    onSuccess(_, variables) {
      const { causeId } = variables;
      const key = studentKeys.id(causeId).fees.all;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

// ----- Publish a Fee -----
const usePublishFee = () => {
  const StudentApi = useStudentApi();
  const queryClient = useQueryClient();

  const publishFee = async ({ causeId, uuid }: { causeId: string; uuid: string }) => {
    const { data } = await StudentApi.put<FeeResponse>(
      `/fees/${uuid}/publish?cause_id=${causeId}`,
      {
        cause_id: causeId,
        uuid,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: publishFee,
    onSuccess(_, variables) {
      const { causeId, uuid } = variables;
      // Invalidate both the individual fee and the fees list
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.id(uuid).detail,
      });
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.all,
      });
    },
  });
};

// ----- Schedule a Fee to be published -----
interface ScheduleFeeRequest {
  causeId: string;
  uuid: string;
  publishedAt: string; // ISO date string
}

const useScheduleFee = () => {
  const StudentApi = useStudentApi();
  const queryClient = useQueryClient();

  const scheduleFee = async ({ causeId, uuid, publishedAt }: ScheduleFeeRequest) => {
    const { data } = await StudentApi.put<FeeResponse>(
      `/fees/${uuid}/schedule?cause_id=${causeId}`,
      {
        fee: {
          published_at: publishedAt,
        },
        cause_id: causeId,
        uuid,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: scheduleFee,
    onSuccess(_, variables) {
      const { causeId, uuid } = variables;
      // Invalidate both the individual fee and the fees list
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.id(uuid).detail,
      });
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.all,
      });
    },
  });
};

// ----- Unpublish a Fee -----
interface UnpublishFeeRequest {
  causeId: string;
  uuid: string;
}

const useUnpublishFee = () => {
  const StudentApi = useStudentApi();
  const queryClient = useQueryClient();

  const unpublishFee = async ({ causeId, uuid }: UnpublishFeeRequest) => {
    const { data } = await StudentApi.put<FeeResponse>(
      `/fees/${uuid}/unpublish?cause_id=${causeId}`,
      {
        cause_id: causeId,
        uuid,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: unpublishFee,
    onSuccess(_, variables) {
      const { causeId } = variables;

      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.all,
      });
    },
  });
};

// ----- Update a Fee -----

const useUpdateFee = () => {
  const StudentApi = useStudentApi();
  const queryClient = useQueryClient();

  const updateFee = async ({ causeId, uuid, fee }: UpdateFeeRequest) => {
    const { data } = await StudentApi.put<FeeResponse>(`/fees/${uuid}?cause_id=${causeId}`, {
      fee,
      cause_id: causeId,
      uuid,
    });
    return data;
  };

  return useMutation({
    mutationFn: updateFee,
    onSuccess(_, variables) {
      const { causeId, uuid } = variables;
      // Invalidate both the individual fee and the fees list
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).studentFees.id(uuid).all,
      });
      void queryClient.invalidateQueries({
        queryKey: studentKeys.id(causeId).fees.all,
      });
    },
  });
};

// ----- Update a Fee Account Code -----
interface UpdateFeeAccountCodeRequest {
  causeId: string;
  storeItemId?: string; // NOTE: This is a hack, actually this is the fee id, but we need to call it storeItemId to pass existing Account Code typing
  accountCode?: string | null;
}

const useUpdateFeeAccountCode = () => {
  const { mutate, ...rest } = useUpdateFee();

  const updateFeeAccountCode = (
    { causeId, storeItemId, accountCode }: UpdateFeeAccountCodeRequest,
    options?: Parameters<typeof mutate>[1]
  ) => {
    mutate(
      {
        causeId,
        uuid: storeItemId ?? '',
        fee: { account_code: accountCode ?? '' },
      },
      options
    );
  };

  return { mutate: updateFeeAccountCode, ...rest };
};

export {
  usePublishFee,
  useScheduleFee,
  useUnarchiveFees,
  useUnpublishFee,
  useUpdateFee,
  useUpdateFeeAccountCode,
};
